import { useState, useEffect, useCallback } from "react";
import { API, BENCHMARKS } from "./constants";
import { SHOPIFY_BRAND_DATA } from "./data";

/**
 * useShopify - Manages Shopify store connection, products, and brand config.
 */
export function useShopify() {
  const [shopConnected, setShopConnected] = useState(false);
  const [shopDomain, setShopDomain]       = useState("");
  const [shopInfo, setShopInfo]           = useState(null);
  const [shopProducts, setShopProducts]   = useState([]);
  const [shopLoading, setShopLoading]     = useState(false);

  const fetchShopInfo = useCallback(async (shop) => {
    try {
      const res = await fetch(`${API}/api/shopify/shop?shop=${encodeURIComponent(shop)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.connected) {
          setShopInfo(data);
          setShopConnected(true);
          setShopDomain(shop);
          return true;
        }
      }
    } catch (_) { /* network error */ }
    return false;
  }, []);

  const fetchShopProducts = useCallback(async (shop) => {
    try {
      const res = await fetch(`${API}/api/products/enriched?shop=${encodeURIComponent(shop)}`);
      if (res.ok) {
        const data = await res.json();
        setShopProducts(data.products || []);
      }
    } catch (_) { /* network error */ }
  }, []);

  const disconnect = useCallback(() => {
    setShopConnected(false);
    setShopDomain("");
    setShopInfo(null);
    setShopProducts([]);
  }, []);

  return {
    shopConnected, shopDomain, shopInfo, shopProducts, shopLoading,
    setShopLoading, fetchShopInfo, fetchShopProducts, disconnect,
  };
}

/**
 * useGoogle - Manages Google OAuth connection state.
 */
export function useGoogle() {
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleInfo, setGoogleInfo]           = useState(null);
  const [analyticsConnected, setAnalyticsConnected] = useState(false);
  const [realCVR, setRealCVR] = useState(BENCHMARKS.CVR);
  const [realAOV, setRealAOV] = useState(BENCHMARKS.AOV);

  const markConnected = useCallback((data) => {
    setGoogleConnected(true);
    setGoogleInfo(data);
    setAnalyticsConnected(true);
  }, []);

  return {
    googleConnected, setGoogleConnected, googleInfo, setGoogleInfo,
    analyticsConnected, setAnalyticsConnected,
    realCVR, setRealCVR, realAOV, setRealAOV,
    markConnected,
  };
}

/**
 * useBrandConfig - Manages brand tone, words, and voice.
 */
export function useBrandConfig() {
  const [tone, setTone]   = useState("technical");
  const [words, setWords] = useState(["cheap", "affordable", "budget", "basic"]);
  const [voice, setVoice] = useState("");

  const save = useCallback(async (shopDomain, demoMode) => {
    if (!shopDomain && !demoMode) return;
    try {
      await fetch(`${API}/api/brand-config?shop=${encodeURIComponent(shopDomain || "demo")}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tone, banned_words: words, voice_notes: voice }),
      });
    } catch (_) { /* network error */ }
  }, [tone, words, voice]);

  const load = useCallback(async (shop) => {
    try {
      const res = await fetch(`${API}/api/brand-config?shop=${encodeURIComponent(shop)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.tone && data.tone !== "professional") setTone(data.tone);
        if (data.banned_words?.length) setWords(data.banned_words);
        if (data.voice_notes) setVoice(data.voice_notes);
      }
    } catch (_) { /* network error */ }
  }, []);

  const reset = useCallback(() => {
    setTone("technical");
    setVoice("");
    setWords(["cheap", "affordable", "budget", "basic"]);
  }, []);

  const applyDemo = useCallback(() => {
    const d = SHOPIFY_BRAND_DATA;
    setTone(d.detectedTone);
    setVoice(d.detectedVoice);
    setWords(d.detectedBannedWords);
  }, []);

  return { tone, setTone, words, setWords, voice, setVoice, save, load, reset, applyDemo };
}

/**
 * useCopyGeneration - Manages AI copy generation and Shopify push flows.
 */
export function useCopyGeneration({ activeProducts, shopDomain, shopConnected, demoMode, appr, fixes, tone, words, voice }) {
  const [generatedCopy, setGeneratedCopy] = useState({});
  const [isGenerating, setIsGenerating]   = useState(false);
  const [genProgress, setGenProgress]     = useState({ done: 0, total: 0, current: "" });
  const [isPushing, setIsPushing]         = useState(false);
  const [pushProgress, setPushProgress]   = useState({ done: 0, total: 0, current: "" });
  const [pushResults, setPushResults]     = useState(null);

  const generate = useCallback(async (selectedIds, onComplete) => {
    const prods = activeProducts.filter(p => selectedIds.includes(p.id));
    if (!prods.length || demoMode || prods.every(p => generatedCopy[p.id])) {
      onComplete();
      return;
    }

    setIsGenerating(true);
    setGenProgress({ done: 0, total: prods.length, current: prods[0]?.title || "" });

    const copy = { ...generatedCopy };
    for (let i = 0; i < prods.length; i++) {
      const p = prods[i];
      if (copy[p.id]) {
        setGenProgress(prev => ({ ...prev, done: i + 1, current: prods[i + 1]?.title || "" }));
        continue;
      }
      setGenProgress({ done: i, total: prods.length, current: p.title });
      try {
        const res = await fetch(`${API}/api/generate-copy`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_id: String(p.id),
            signals: {
              GSC: p.gscQueries || [], GA4: p.ga4Terms || [],
              SEM: p.semKeywords || [], Social: p.socialPhrases || [],
              Trends: p.trendTerms || [], Reddit: p.redditPhrases || [],
            },
            tone, banned_words: words, voice_notes: voice,
            product_type: p.shopifyData?.productType || p.product_type || "",
            tags: p.shopifyData?.tags || [],
            metafields: p.shopifyData?.metafields || {},
            geo_targets: ["UK"],
          }),
        });
        if (res.ok) {
          const data = await res.json();
          copy[p.id] = {
            optimised_title: data.optimized_title,
            optimised_description: data.optimized_description,
            meta_title: data.meta_title,
            meta_description: data.meta_description,
            tags: data.tags || [],
            shopping_title: data.shopping_title || "",
            shopping_description: data.shopping_description || "",
            seo_title: data.seo_title || "",
            seo_meta_description: data.seo_meta_description || "",
            seo_h1: data.seo_h1 || "",
            seo_long_description: data.seo_long_description || "",
            geo_targets: data.geo_targets || ["UK"],
            gmc_attributes: data.gmc_attributes || {},
            channel_titles: data.channel_titles || {},
          };
        }
      } catch (_) { /* generation error */ }
    }
    setGeneratedCopy(copy);
    setGenProgress({ done: prods.length, total: prods.length, current: "" });
    setIsGenerating(false);
    onComplete();
  }, [demoMode, generatedCopy, tone, words, voice, activeProducts]);

  const push = useCallback(async (onComplete) => {
    const prods = activeProducts.filter(p => appr.includes(p.id));
    if (!prods.length) { onComplete(); return; }

    if (demoMode || !shopConnected) {
      setPushResults({
        success: true, pushed: prods.length, total: prods.length,
        pushed_at: new Date().toISOString(), demo: true,
        results: prods.map(p => ({
          id: String(p.id), title: p.title, success: true,
          changes: ["title", "description", "meta_title", "meta_description", "tags"],
        })),
      });
      onComplete();
      return;
    }

    setIsPushing(true);
    setPushProgress({ done: 0, total: prods.length, current: prods[0]?.title || "" });

    const productsPayload = prods.map(p => {
      const copy = generatedCopy[p.id] || {};
      const gmcFixes = {};
      Object.entries(fixes).forEach(([k, v]) => {
        if (k.startsWith(`${p.id}-`) && v) {
          const field = k.substring(String(p.id).length + 1);
          const issue = p.gmcIssues?.find(gi => gi.field === field);
          if (issue) gmcFixes[field] = issue.fix || v;
        }
      });
      return {
        id: String(p.id), shopify_id: p.shopify_id || p.id,
        title: p.title,
        optimisedTitle: copy.optimised_title || p.optimisedTitle || p.title,
        optimisedDesc: copy.optimised_description || p.optimisedDesc || p.description || "",
        metaTitle: copy.meta_title || `${p.title} | ${p.brand} | UK`,
        metaDesc: copy.meta_description || `Shop the ${p.title} from ${p.brand}. Free UK delivery.`,
        tags: [...(p.shopifyData?.tags || []), ...(p.signalsUsed || [])],
        gmcFixes,
      };
    });

    try {
      const res = await fetch(`${API}/api/shopify/products/push`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop: shopDomain, products: productsPayload }),
      });
      if (res.ok) {
        setPushResults(await res.json());
      } else {
        const errText = await res.text().catch(() => "Unknown error");
        setPushResults({
          success: false, pushed: 0, total: prods.length,
          pushed_at: new Date().toISOString(), error: errText,
          results: prods.map(p => ({ id: String(p.id), title: p.title, success: false, error: errText })),
        });
      }
    } catch (e) {
      setPushResults({
        success: false, pushed: 0, total: prods.length,
        pushed_at: new Date().toISOString(), error: e.message,
        results: prods.map(p => ({ id: String(p.id), title: p.title, success: false, error: e.message })),
      });
    }

    setIsPushing(false);
    setPushProgress({ done: prods.length, total: prods.length, current: "" });
    onComplete();
  }, [demoMode, shopConnected, shopDomain, activeProducts, appr, fixes, generatedCopy]);

  const resetCopy = useCallback(() => {
    setGeneratedCopy({});
    setPushResults(null);
  }, []);

  return {
    generatedCopy, isGenerating, genProgress,
    isPushing, pushProgress, pushResults,
    generate, push, resetCopy,
  };
}

/**
 * useInitialLoad - Handles URL params on mount (OAuth callbacks, demo mode, Shopify install).
 */
export function useInitialLoad({ fetchShopInfo, fetchShopProducts, loadBrandConfig, setShopLoading, markGoogleConnected, startDemo }) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shop = params.get("shop");
    const connected = params.get("connected");
    const demo = params.get("demo");
    const googleConn = params.get("google_connected");
    const hmacParam = params.get("hmac");

    if (demo === "true" || demo === "lite") {
      window.history.replaceState({}, "", window.location.pathname);
      startDemo(demo === "lite" ? "lite" : "full");
    } else if (googleConn === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      fetch(`${API}/api/google/connection-status?shop=${encodeURIComponent(shop || "")}`)
        .then(r => r.json())
        .then(data => { if (data.connected) markGoogleConnected(data); })
        .catch(() => {});
    } else if (shop && connected === "true") {
      setShopLoading(true);
      window.history.replaceState({}, "", window.location.pathname);
      fetchShopInfo(shop).then((ok) => {
        if (ok) { fetchShopProducts(shop); loadBrandConfig(shop); }
        setShopLoading(false);
      });
    } else if (shop && hmacParam && !connected) {
      window.history.replaceState({}, "", window.location.pathname);
      setShopLoading(true);
      fetch(`${API}/api/shopify/oauth/start?shop=${encodeURIComponent(shop)}`)
        .then(r => r.json())
        .then(data => {
          if (data.auth_url) window.location.href = data.auth_url;
          else setShopLoading(false);
        })
        .catch(() => { setShopLoading(false); });
    } else {
      fetch(`${API}/api/shopify/connection-status`)
        .then(r => r.json())
        .then(data => {
          if (data.connected && data.shop) {
            setShopLoading(true);
            fetchShopInfo(data.shop).then((ok) => {
              if (ok) { fetchShopProducts(data.shop); loadBrandConfig(data.shop); }
              setShopLoading(false);
            });
          }
        })
        .catch(() => {});

      fetch(`${API}/api/google/connection-status`)
        .then(r => r.json())
        .then(data => { if (data.connected) markGoogleConnected(data); })
        .catch(() => {});
    }
  // Run once on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
